import axios from "axios";

export type AnnouncementAudiences = {
	client?: boolean;
	clientAdmin?: boolean;
	provider?: boolean;
	staff?: boolean;
	staffRoleIds?: string[];
};

export type Announcement = {
	id: string;
	title: string;
	body: string;
	actionLabel: string | null;
	actionPath: string | null;
	moreUrl: string | null;
	audiences: AnnouncementAudiences;
	startsAt: string | null;
	endsAt: string | null;
	status: "draft" | "published" | "archived";
	publishedAt?: string | null;
	createdAt?: string;
	updatedAt?: string;
	stats?: { shown: number; reachedEnd: number; clicked: number; acknowledged: number; deferred: number };
};

export type AnnouncementEventType = "shown" | "later" | "reached_end" | "clicked" | "acknowledged" | "closed";

export type AnnouncementPerson = {
	userId: string;
	name: string;
	email: string;
	organization: string | null;
	audience: string | null;
	firstShownAt: string | null;
	lastShownAt: string | null;
	timesShown: number;
	laterCount: number;
	reachedEndAt: string | null;
	clickedAt: string | null;
	acknowledgedAt: string | null;
	closedAt: string | null;
};

export type AnnouncementHistoryItem = {
	announcementId: string;
	title: string;
	actionLabel: string | null;
	firstShownAt: string | null;
	lastShownAt: string | null;
	timesShown: number;
	laterCount: number;
	reachedEndAt: string | null;
	clickedAt: string | null;
	acknowledgedAt: string | null;
	closedAt: string | null;
};

const fail = (e: any, what: string) => ({ error: e?.response?.data?.message ? String(e.response.data.message) : `${what} failed.` });

/** The one announcement to show this person now (or null). Never throws. */
export const getNextAnnouncement = async (): Promise<Announcement | null> => {
	try {
		const res = await axios.get(`/announcements/next`);
		return res?.data?.announcement ?? null;
	} catch {
		return null;
	}
};

/** Fire-and-forget viewer event. */
export const sendAnnouncementEvent = async (id: string, type: AnnouncementEventType) => {
	try {
		await axios.post(`/announcements/${id}/events`, { type });
	} catch {
		/* tracking never blocks the user */
	}
};

export const listAnnouncements = async (): Promise<Announcement[] | { error: string }> => {
	try {
		const res = await axios.get(`/announcements`);
		return Array.isArray(res.data) ? res.data : [];
	} catch (e: any) {
		return fail(e, "Loading announcements");
	}
};

export const createAnnouncement = async (data: Partial<Announcement>): Promise<Announcement | { error: string }> => {
	try {
		return (await axios.post(`/announcements`, data)).data;
	} catch (e: any) {
		return fail(e, "Saving");
	}
};

export const updateAnnouncement = async (id: string, data: Partial<Announcement>): Promise<Announcement | { error: string }> => {
	try {
		return (await axios.put(`/announcements/${id}`, data)).data;
	} catch (e: any) {
		return fail(e, "Saving");
	}
};

export const publishAnnouncement = async (id: string): Promise<Announcement | { error: string }> => {
	try {
		return (await axios.post(`/announcements/${id}/publish`)).data;
	} catch (e: any) {
		return fail(e, "Publishing");
	}
};

export const archiveAnnouncement = async (id: string): Promise<Announcement | { error: string }> => {
	try {
		return (await axios.post(`/announcements/${id}/archive`)).data;
	} catch (e: any) {
		return fail(e, "Archiving");
	}
};

export const duplicateAnnouncement = async (id: string): Promise<Announcement | { error: string }> => {
	try {
		return (await axios.post(`/announcements/${id}/duplicate`)).data;
	} catch (e: any) {
		return fail(e, "Duplicating");
	}
};

export const getAnnouncementEngagement = async (id: string): Promise<{ announcement: Announcement; people: AnnouncementPerson[] } | { error: string }> => {
	try {
		return (await axios.get(`/announcements/${id}/engagement`)).data;
	} catch (e: any) {
		return fail(e, "Loading engagement");
	}
};

export const getUserAnnouncementHistory = async (userId: string): Promise<AnnouncementHistoryItem[]> => {
	try {
		const res = await axios.get(`/announcements/users/${userId}/history`);
		return Array.isArray(res.data) ? res.data : [];
	} catch {
		return [];
	}
};

export const getAnnouncementSettings = async (): Promise<{ enabled: boolean } | { error: string }> => {
	try {
		return (await axios.get(`/announcements/settings`)).data;
	} catch (e: any) {
		return fail(e, "Loading");
	}
};

export const setAnnouncementSettings = async (enabled: boolean): Promise<{ enabled: boolean } | { error: string }> => {
	try {
		return (await axios.put(`/announcements/settings`, { enabled })).data;
	} catch (e: any) {
		return fail(e, "Saving");
	}
};
